// Dependencies
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Link } from "gatsby";
import styled from "@emotion/styled";
import { RichText } from "prismic-reactjs";
import Moment from 'react-moment';

// Styles
import colors from "styles/colors";
import dimensions from "styles/dimensions";

const YearDivider = styled("div")`
    display: flex;
    align-items: center;
    margin-top: 1em;
    margin-bottom: 2em;
    border-bottom: 3px solid ${colors.grey300};

    span {
        padding-left: 0.25em;
        padding-right: 0.25em;
        margin-bottom: 0.25em;
        color: ${colors.grey500};
    }
`

const PostCardContainer = styled("div")`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 2.5em;
    gap: 2em;

    @media (max-width: ${dimensions.maxwidthTablet}px) {
        flex-direction: column-reverse;
        justify-content: flex-start;
        gap: 0.25em;
    }
`

const PostText = styled("div")`
    width: fill-available;
    display: flex;
    flex-direction: column;

    a {
        width: fit-content;
        display: inline;
        color: currentColor;
        text-decoration: none;
    }
`

const PostTitle = styled("div")`
    display: inline-block;
    padding-bottom: 0.3em; // Use instead of gap for hover

    @media (max-width: ${dimensions.maxwidthTablet}px) {
        padding-bottom: 0.45em;
    }

    h3 {
        margin: 0;
        font-size: 1.2em;
    }

    // Square bullet point that appears on hover
    h3 > span {
        transition: all 175ms ease-in-out;
        margin-left: 0.25em;

        // Appear on hover
        opacity: ${(props) => props.isHovered ? "1" : "0"};

        // Set its color dynamically based on blog post category
        color: ${(props) => getColorset(props.category)[2]};
    }
`

const PostDescription = styled("div")`
    width: fit-content;

    a {
        color: currentColor;
        text-decoration: none;
        transition: all 150ms ease-in-out;

        color: ${(props) => props.isHovered ? colors.bqblue500 : "currentColor"};
        background: ${(props) => props.isHovered ? colors.bqblue200 : "transparent"};
    }

    p {
        padding: 0;
        margin: 0;
        display: inline;
        line-height: 1.6em;
    }

    span {
        margin-left: 0.5em;
        transform: ${(props) => props.isHovered ? "translateX(0px)" : "translateX(-6px)"};
        display: inline-block;
        transition: ${(props) => props.isHovered ? "transform 150ms ease-in-out" : "transform 250ms ease-in-out"}; // Fast out, slow return
        line-height: 0;

        color: ${colors.bqblue500};
    }
`

const PostInfo = styled("div")`
    width: 25%;
    display: flex;
    flex-direction: column;
    gap: 0.3em;

    @media (min-width: ${dimensions.minwidthXLscreen}px) {
        width: 20%;
    }
`

const PostDate = styled("div")`
    font-family: 'JetBrains Mono', 'Courier New', Courier, monospace;
    font-size: 0.8em;
    font-weight: 400; // Override Manrope's default 500
    color: ${colors.grey700};
    padding: 0 0.1em; // Match the padding from post category
`

const PostCategory = styled("div")`
    display: flex;

    h5 {
        margin: 0;
        display: inline-block;

        // Highlighting that spans line-breaks
        span {
            display: inline;
            padding: 0 0.15em;
            line-height: 1.6;

            // Receives state via prop
            // These properties change on state change:
            transition: all 150ms ease-in-out;
            color: ${(props) => props.isHovered ? getColorset(props.category)[0] : "currentColor"};
            background: ${(props) => props.isHovered ? getColorset(props.category)[1] : "transparent"};
        }

        @media (max-width: ${dimensions.maxwidthTablet}px) {
            display: none;
        }
    }
`

// Given blog post category, returns correct color tuple (text, background, color)
export const getColorset = (category) => {
    if (category === "Personal growth") {
        return [colors.purple600, colors.purple200, colors.purple500];
    } else if (category === "Reflection") {
        return [colors.green600, colors.green200, colors.green500];
    } else if (category === "Observation") {
        return [colors.orange600, colors.orange200, colors.orange500];
    }
};

// Function to take title string, and return
// all words lowercase except for first word
const formatTitleString = (str) => {
    let output = [];
    output.push(str.charAt(0));

    for (let i = 1; i < str.length; i++) {
        // If letter is a single capital I, do not make lowercase
        if (str.charAt(i) === "I" && str.charAt(i+1) === " "
        && str.charAt(i-1) === " ") {
            output.push(str.charAt(i));
            continue;
        }

        // Else make letter lowercase
        output.push(str.charAt(i).toLowerCase());
    }

    return output.join("");
}

const PostCard = ({ author, category, date, title, description, uid }) => {
    // Create a new state: hovered
    const [isHovered, setIsHovered] = useState(false);

    // These functions just take care of setting/changing the state when actions occur
    const handleHover = () => {
        setIsHovered(true);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
    };

    return (
        <>
            {
                // Hardcoding a divider between years
                (date === "2023-10-11" || date === "2020-05-29" || date === "2019-12-31") ? 
                    <YearDivider>
                        <span>{date.substring(0, 4)}</span>
                    </YearDivider> 
                    : <></>
            }

            <PostCardContainer>

                <PostText>
                    {/* This class is responsible for triggering changes to state onMouseOver*/}
                    <Link to={`/blog/${uid}`} onMouseOver={handleHover} onMouseLeave={handleMouseLeave}>
                        {/* Passing category as prop to determine color of box*/}
                        <PostTitle category={category[0].text} isHovered={isHovered}>
                            <h3>
                                {formatTitleString(title[0].text)}
                                <span>&#9642;</span>
                            </h3>
                        </PostTitle>
                    </Link>
                    
                    <PostDescription isHovered={isHovered}>
                        {/* This should also trigger changes to state on mouseover */}
                        <Link to={`/blog/${uid}`} onMouseOver={handleHover} onMouseLeave={handleMouseLeave}>
                            {RichText.render(description)}
                            <span>&#8594;</span>
                        </Link>
                    </PostDescription>
                </PostText>
                
                <PostInfo>
                    <PostDate>
                        <Moment format="MM.DD.YYYY">{date}</Moment>
                    </PostDate>

                    {/* Now this class can react to changes in state, which we pass
                        to the styled component as a prop "isHovered"

                        We also pass the category as props to dynamically set the color
                    */}
                    <PostCategory isHovered={isHovered} category={category[0].text}>
                        <h5>
                            <span>{category[0].text}</span>
                        </h5>
                    </PostCategory>
                </PostInfo>
                
            </PostCardContainer>
        </>
    );
}

export default PostCard;

// Ensure prop types
PostCard.propTypes = {
    author: PropTypes.string.isRequired,
    category: PropTypes.array.isRequired,
    date: PropTypes.string.isRequired,
    title: PropTypes.array.isRequired,
    description: PropTypes.array.isRequired,
    uid: PropTypes.string.isRequired
}